/**
 * AtlasLocalMapModule — the SWG-style LOCAL map (M): an overhead view of the
 * player's surroundings with entities and structures, the planetary-map/
 * overlay-map read of Star Wars Galaxies (CTRL+M superimposed the surrounding
 * buildings; the radar showed nearby life as colored dots).
 *
 * Layers (bottom → top):
 *   terrain underlay  async-sliced biome/height image (the World Atlas pattern)
 *   settlement layer  built buildings/landmarks (CommonsSettlements label
 *                     anchors) + the port spots (shuttle pad ▲, spaceport ◆,
 *                     work terminal ●)
 *   deed structures   `structure` rows in the current world (player houses,
 *                     harvesters, guild halls)
 *   entities          SWG radar colors — players cyan, versonas/NPCs yellow,
 *                     creatures red — plus the white player arrow (heading-up
 *                     stays OFF: the map is north-up like SWG's, the arrow
 *                     rotates instead)
 *
 * The view FOLLOWS the player until dragged; ⌖ recenters. Display only —
 * every row is replicated state; nothing here writes.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { settlementSites, type SettlementSite } from '@shared/settlements.js';
import { terrainHeight, biomeAt } from '../../../shared/terrain.js';
import { forestDensity } from '../lib/forest_field.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { spaceportSpot, shuttleSpot, terminalSpot } from '../lib/ports.js';
import { settlementNamesFor } from '../lib/settlement_names.js';
import { townSlots } from '@shared/town_layout.js';
import { civilizationOf } from '../lib/biome_settlements.js';
import type { Civilization } from '../lib/biome_settlements.js';
import { civOfSettlementRowC, civRowGeneration } from '../lib/civ_row.js';
import type { CommonsSettlementsModule } from './CommonsSettlementsModule.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import { myWaypoints, promptCreateWaypoint, promptEditWaypoint, drawWaypointMarker } from '../lib/waypoints.js';

const rowFirstCiv = (siteSeed: number, siteId?: number): Civilization =>
  siteId === undefined ? civilizationOf(siteSeed) : civOfSettlementRowC(BigInt(siteId + 1), siteSeed);

/** 'structure:civa_habitat_tower_scifi2' → 'Habitat Tower' (label prettifier). */
function prettyKey(key: string): string {
  const slug = key.replace(/^(structure|prop|foliage):/, '').replace(/^civ[ab]_/, '')
    .replace(/_?(scifi|city)?\d*$/, '').replace(/_/g, ' ').trim();
  return slug.replace(/\b\w/g, (c) => c.toUpperCase());
}

interface PlayerRow { identity: { toHexString(): string }; worldId: bigint; posX: number; posZ: number }
interface CreatureRow { creatureId: bigint; x: number; z: number; state: string }
interface StructureRow { structureId: bigint; worldId: bigint; posX: number; posZ: number; structureType: string }

const BIOME_COLOR: Record<string, [number, number, number]> = {
  regolith_plains: [96, 88, 74], crystal_forest: [88, 74, 116], dune_desert: [128, 108, 70],
  rocky_highlands: [84, 82, 86], moss_wetland: [58, 88, 62], fungal_basin: [60, 92, 92],
};

export class AtlasLocalMapModule implements IModule {
  readonly name = 'AtlasLocalMap';

  private app: App | null = null;
  private win: UiWindow | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private wrap: HTMLDivElement | null = null;

  private seed = '';
  private civGen = -1;
  private sea = 0;
  private sites: SettlementSite[] = [];
  private siteNames = new Map<number, string>();

  // SWG CTRL+M in-world overlay: translucent footprints + labels drawn over
  // the 3D view on a pointer-transparent full-screen canvas.
  private overlayOn = false;
  private overlayCanvas: HTMLCanvasElement | null = null;
  private overlayRaf = 0;
  private overlayLast = 0;
  private overlayLabels = 0; // last drawn label count (probe/diagnostics)
  // Named-venue map layer caches (the slots are pure per-site derivations).
  private venueSiteId = -2;
  private venueSlots: import('@shared/town_layout.js').TownSlot[] | null = null;
  private venueCiv: string = 'a';
  private overlayBtn: HTMLButtonElement | null = null;

  /** px per meter (wheel-zoomable); follow = view glued to the player. */
  private scale = 1.15;
  private follow = true;
  private cx = 0;
  private cz = 0;

  private dirty = true;
  private raf = 0;
  private timer: number | null = null;

  // Terrain underlay (async-sliced, the World Atlas pattern).
  private under: { img: HTMLCanvasElement; x0: number; z0: number; x1: number; z1: number } | null = null;
  private underKey = '';
  private underBuilding = '';

  init(app: App): void {
    this.app = app;
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyM' || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.currentWorldId === null) return;
      if (atlas.spaceFlight) return; // M is the galactic chart in the sector
      if (e.ctrlKey) { e.preventDefault(); this.toggleOverlay(); return; } // SWG CTRL+M
      this.toggle();
    });
    // Diagnostic surface (headless probes): open + read the layer counts.
    (window as unknown as Record<string, unknown>).__localMap = {
      open: (): void => { if (!this.win?.isOpen()) this.toggle(); },
      overlay: (on: boolean): void => { if (on !== this.overlayOn) this.toggleOverlay(); },
      state: (): Record<string, unknown> => {
        const p = this.gather();
        return {
          open: !!this.win?.isOpen(), underBuilt: this.underKey !== '',
          overlayOn: this.overlayOn, overlayLabels: this.overlayLabels,
          buildings: p.buildings.length, ports: p.ports.length,
          deeds: p.deeds.length, players: p.players.length,
          versonas: p.versonas.length, creatures: p.creatures.length,
        };
      },
    };
  }

  private ensureWorld(): boolean {
    const conn = atlas.conn;
    if (!conn) return false;
    for (const w of conn.db.world.iter()) {
      if (w.kind !== 'commons') continue;
      if (this.seed !== w.terrainSeed || this.civGen !== civRowGeneration()) {
        this.civGen = civRowGeneration();
        this.seed = w.terrainSeed;
        this.sites = settlementSites(this.seed);
        this.sea = commonsSeaLevel(this.seed);
        this.siteNames = settlementNamesFor(this.sites, rowFirstCiv);
      }
      return true;
    }
    return false;
  }

  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); this.stopLoops(); return; }
    if (!this.ensureWorld()) return;
    if (!this.win) this.build();
    this.follow = true;
    this.win!.setOpen(true);
    this.dirty = true;
    this.startLoops();
  }

  private build(): void {
    this.win = makeWindow({
      id: 'local-map', title: 'Local Map', icon: 'map',
      x: 60, y: 60, width: 520, height: 560, minWidth: 320, minHeight: 320,
      onClose: () => this.stopLoops(),
    });
    const root = document.createElement('div');
    root.style.cssText = 'display:flex;flex-direction:column;height:100%;min-height:0';

    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:6px;align-items:center;padding:6px 8px;border-bottom:1px solid #24344a;font:11px system-ui;color:#cde';
    const legend = document.createElement('div');
    legend.innerHTML =
      '<span style="color:#6fe0ff">●</span> player&nbsp; <span style="color:#e8d54a">●</span> versona&nbsp; ' +
      '<span style="color:#e5534b">●</span> creature&nbsp; <span style="color:#b48ae8">■</span> deed&nbsp; ' +
      '<span style="color:#9fb2c8">■</span> building&nbsp; <span style="color:#e8a94e">▲</span> shuttle&nbsp; ' +
      '<span style="color:#ffd166">◆</span> waypoint&nbsp; ' +
      '<span style="opacity:.6">scroll to zoom · drag to pan · right-click sets/edits a waypoint</span>';
    legend.style.cssText = 'flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;opacity:.92';
    bar.appendChild(legend);
    const meBtn = document.createElement('button');
    meBtn.textContent = '⌖ me';
    meBtn.title = 'Follow your position';
    meBtn.style.cssText = 'background:#1c2a3c;color:#cde;border:1px solid #35506a;border-radius:6px;padding:2px 9px;cursor:pointer';
    meBtn.onclick = () => { this.follow = true; this.dirty = true; };
    bar.appendChild(meBtn);
    this.overlayBtn = document.createElement('button');
    this.overlayBtn.textContent = '⿻ overlay';
    this.overlayBtn.title = 'In-world overlay — footprints & labels over the 3D view (Ctrl+M)';
    this.overlayBtn.style.cssText = meBtn.style.cssText;
    this.overlayBtn.onclick = () => this.toggleOverlay();
    this.styleOverlayBtn();
    bar.appendChild(this.overlayBtn);
    root.appendChild(bar);

    this.wrap = document.createElement('div');
    this.wrap.style.cssText = 'flex:1 1 auto;position:relative;overflow:hidden;background:#060a12;min-height:0';
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;cursor:crosshair';
    this.wrap.appendChild(this.canvas);
    root.appendChild(this.wrap);
    this.win.body.appendChild(root);

    // Wheel zoom (about the cursor) + drag pan (breaks follow).
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const f = e.deltaY < 0 ? 1.2 : 1 / 1.2;
      const rect = this.canvas!.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const wxAt = this.wx(mx), wzAt = this.wz(my);
      this.scale = Math.min(6, Math.max(0.18, this.scale * f));
      this.cx = wxAt - (mx - this.cw() / 2) / this.scale;
      this.cz = wzAt - (my - this.ch() / 2) / this.scale;
      this.follow = false;
      this.dirty = true;
    }, { passive: false });
    let drag: { px: number; py: number; cx: number; cz: number } | null = null;
    this.canvas.addEventListener('pointerdown', (e) => {
      drag = { px: e.clientX, py: e.clientY, cx: this.cx, cz: this.cz };
      this.canvas!.setPointerCapture(e.pointerId);
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (!drag) return;
      this.cx = drag.cx - (e.clientX - drag.px) / this.scale;
      this.cz = drag.cz - (e.clientY - drag.py) / this.scale;
      this.follow = false;
      this.dirty = true;
    });
    this.canvas.addEventListener('pointerup', () => { drag = null; });
    // Right-click: edit the waypoint under the cursor, or set a new one here
    // (the World Atlas contract — both maps speak the same gesture).
    this.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      const conn = atlas.conn;
      if (!conn) return;
      const rect = this.canvas!.getBoundingClientRect();
      const px = e.clientX - rect.left, py = e.clientY - rect.top;
      let hitW: ReturnType<typeof myWaypoints>[0] | null = null;
      let bestD = 12;
      for (const w of myWaypoints(conn)) {
        const d = Math.hypot(this.sx(w.posX) - px, this.sz(w.posZ) - py);
        if (d < bestD) { bestD = d; hitW = w; }
      }
      if (hitW) promptEditWaypoint(conn, hitW);
      else promptCreateWaypoint(conn, this.wx(px), this.wz(py));
      this.dirty = true;
    });

    new ResizeObserver(() => { this.resizeCanvas(); }).observe(this.wrap);
    this.resizeCanvas();
  }

  private startLoops(): void {
    if (!this.raf) {
      const loop = (): void => {
        this.raf = this.win?.isOpen() ? requestAnimationFrame(loop) : 0;
        if (this.dirty && this.win?.isOpen()) { this.dirty = false; this.draw(); }
      };
      this.raf = requestAnimationFrame(loop);
    }
    if (this.timer === null) {
      // Entities move constantly — refresh at 4 Hz while open.
      this.timer = window.setInterval(() => {
        if (!this.win?.isOpen()) { this.stopLoops(); return; }
        this.dirty = true;
      }, 250);
    }
  }

  private stopLoops(): void {
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
  }

  private resizeCanvas(): void {
    const c = this.canvas;
    if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.max(1, Math.floor(c.clientWidth * dpr));
    c.height = Math.max(1, Math.floor(c.clientHeight * dpr));
    this.dirty = true;
  }

  private cw(): number { return this.canvas?.clientWidth ?? 1; }
  private ch(): number { return this.canvas?.clientHeight ?? 1; }
  private sx(wx: number): number { return (wx - this.cx) * this.scale + this.cw() / 2; }
  private sz(wz: number): number { return (wz - this.cz) * this.scale + this.ch() / 2; }
  private wx(px: number): number { return (px - this.cw() / 2) / this.scale + this.cx; }
  private wz(py: number): number { return (py - this.ch() / 2) / this.scale + this.cz; }

  // ── Terrain underlay (async-sliced; the World Atlas pattern) ───────────────
  private kickUnderlay(): void {
    const N = 128;
    const w = this.cw() / this.scale, h = this.ch() / this.scale;
    const x0 = this.cx - w / 2, z0 = this.cz - h / 2;
    const key = `${Math.round(x0 / 60)}:${Math.round(z0 / 60)}:${Math.round(Math.log2(this.scale) * 6)}`;
    if (this.underKey === key || this.underBuilding === key) return;
    this.underBuilding = key;
    const img = document.createElement('canvas');
    img.width = N; img.height = N;
    const g = img.getContext('2d')!;
    const px = g.createImageData(N, N);
    const seed = this.seed, sea = this.sea;
    let row = 0;
    const slice = (): void => {
      if (this.underBuilding !== key) return;
      const t0 = performance.now();
      while (row < N && performance.now() - t0 < 7) {
        for (let i = 0; i < N; i++) {
          const wxp = x0 + ((i + 0.5) / N) * w, wzp = z0 + ((row + 0.5) / N) * h;
          const ht = terrainHeight(seed, wxp, wzp);
          let r: number, gg: number, b: number;
          if (ht <= sea) {
            const d = Math.min(1, (sea - ht) / 60);
            r = 18 + 12 * (1 - d); gg = 42 + 24 * (1 - d); b = 74 + 34 * (1 - d);
          } else {
            const c = BIOME_COLOR[biomeAt(seed, wxp, wzp)] ?? [90, 90, 90];
            const lum = Math.min(1.45, 0.72 + (ht - sea) / 900);
            // THE WOODS ON THE MAP (flora update 2026-09-07): forest density tints the pixel toward canopy
            // green so stands read at every zoom — the same field that plants the trees.
            const fd = forestDensity(seed, wxp, wzp, sea);
            const fm = fd > 0.45 ? Math.min(1, (fd - 0.45) * 2.2) * 0.62 : 0;
            r = c[0] * lum * (1 - fm) + 30 * fm; gg = c[1] * lum * (1 - fm) + 96 * fm; b = c[2] * lum * (1 - fm) + 42 * fm;
          }
          const o = (row * N + i) * 4;
          px.data[o] = r; px.data[o + 1] = gg; px.data[o + 2] = b; px.data[o + 3] = 255;
        }
        row++;
      }
      if (row < N) { setTimeout(slice, 0); return; }
      g.putImageData(px, 0, 0);
      this.under = { img, x0, z0, x1: x0 + w, z1: z0 + h };
      this.underKey = key;
      this.underBuilding = '';
      this.dirty = true;
    };
    setTimeout(slice, 0);
  }

  // ── Layer gathering (also the probe's state read) ──────────────────────────
  private gather(radiusOverride?: number, centerX?: number, centerZ?: number): {
    buildings: Array<{ key: string; x: number; z: number }>;
    ports: Array<{ x: number; z: number; kind: 'shuttle' | 'spaceport' | 'terminal'; name: string }>;
    deeds: Array<{ x: number; z: number; type: string }>;
    players: Array<{ x: number; z: number }>;
    versonas: Array<{ x: number; z: number }>;
    creatures: Array<{ x: number; z: number }>;
  } {
    const R = radiusOverride ?? (Math.max(this.cw(), this.ch()) / this.scale * 0.75 + 60);
    const px = centerX ?? (this.follow ? atlas.localX : this.cx);
    const pz = centerZ ?? (this.follow ? atlas.localZ : this.cz);
    const out = {
      buildings: [] as Array<{ key: string; x: number; z: number }>,
      ports: [] as Array<{ x: number; z: number; kind: 'shuttle' | 'spaceport' | 'terminal'; name: string }>,
      deeds: [] as Array<{ x: number; z: number; type: string }>,
      players: [] as Array<{ x: number; z: number }>,
      versonas: [] as Array<{ x: number; z: number }>,
      creatures: [] as Array<{ x: number; z: number }>,
    };
    const settle = this.app?.getModule<CommonsSettlementsModule>('CommonsSettlements');
    if (settle) out.buildings = settle.structuresNear(px, pz, R);
    if (this.seed) {
      for (const s of this.sites) {
        if (Math.hypot(s.centerX - px, s.centerZ - pz) > R + 300) continue;
        const name = this.siteNames.get(s.id) ?? '';
        const shut = shuttleSpot(this.seed, s);
        if (shut) out.ports.push({ x: s.centerX + shut.x, z: s.centerZ + shut.z, kind: 'shuttle', name });
        const sport = spaceportSpot(this.seed, s);
        if (sport) out.ports.push({ x: s.centerX + sport.x, z: s.centerZ + sport.z, kind: 'spaceport', name });
        const term = terminalSpot(this.seed, s);
        if (term) out.ports.push({ x: s.centerX + term.x, z: s.centerZ + term.z, kind: 'terminal', name });
      }
    }
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (conn && wid !== null) {
      try {
        for (const s of (conn.db as unknown as { structure: { iter(): Iterable<StructureRow> } }).structure.iter()) {
          if (s.worldId !== wid) continue;
          if (Math.hypot(s.posX - px, s.posZ - pz) <= R) out.deeds.push({ x: s.posX, z: s.posZ, type: s.structureType });
        }
      } catch { /* table not replicated yet */ }
      try {
        const meHex = conn.identity?.toHexString();
        for (const p of (conn.db as unknown as { playerState: { iter(): Iterable<PlayerRow> } }).playerState.iter()) {
          if (p.worldId !== wid || p.identity.toHexString() === meHex) continue;
          if (Math.hypot(p.posX - px, p.posZ - pz) <= R) out.players.push({ x: p.posX, z: p.posZ });
        }
      } catch { /* not replicated */ }
      try {
        for (const c of (conn.db as unknown as { creature: { iter(): Iterable<CreatureRow> } }).creature.iter()) {
          if (c.state === 'dead') continue;
          if (Math.hypot(c.x - px, c.z - pz) <= R) out.creatures.push({ x: c.x, z: c.z });
        }
      } catch { /* not replicated */ }
    }
    const env = this.app?.getModule<EnvironmentModule>('Environment');
    if (env) {
      for (const v of env.versonaPositions()) {
        if (Math.hypot(v.x - px, v.z - pz) <= R) out.versonas.push({ x: v.x, z: v.z });
      }
    }
    return out;
  }

  // ── The SWG CTRL+M in-world overlay ────────────────────────────────────────
  private styleOverlayBtn(): void {
    if (!this.overlayBtn) return;
    this.overlayBtn.style.background = this.overlayOn ? '#2c4a72' : '#1c2a3c';
    this.overlayBtn.style.borderColor = this.overlayOn ? '#6b9fe8' : '#35506a';
  }

  private toggleOverlay(): void {
    this.overlayOn = !this.overlayOn;
    this.styleOverlayBtn();
    if (this.overlayOn) {
      if (!this.ensureWorld()) { this.overlayOn = false; this.styleOverlayBtn(); return; }
      if (!this.overlayCanvas) {
        const c = document.createElement('canvas');
        // Below the UI windows (z 40+) and menu bar, above the 3D canvas;
        // pointer-transparent so the game keeps every click.
        c.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:30';
        document.body.appendChild(c);
        this.overlayCanvas = c;
      }
      this.overlayCanvas.style.display = '';
      this.overlayLast = 0;
      if (!this.overlayRaf) {
        const loop = (): void => {
          this.overlayRaf = this.overlayOn ? requestAnimationFrame(loop) : 0;
          if (!this.overlayOn) return;
          const now = performance.now();
          if (now - this.overlayLast >= 66) { this.overlayLast = now; this.drawOverlay(); }
        };
        this.overlayRaf = requestAnimationFrame(loop);
      }
    } else {
      if (this.overlayCanvas) {
        this.overlayCanvas.getContext('2d')?.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
        this.overlayCanvas.style.display = 'none';
      }
      this.overlayLabels = 0;
    }
  }

  /** Heading-up screen-space map drawn over the 3D view: translucent green
   *  footprints + white labels (the SWG overlay-map look), entities as radar
   *  dots, the player at screen center. */
  private drawOverlay(): void {
    const c = this.overlayCanvas;
    if (!c) return;
    // No loadingGate check: the load screen (z 60) already covers the overlay
    // (z 30), and gating here blanked the layer for headless probes whose
    // load screen never dismisses on a saturated tab.
    if (atlas.currentWorldId === null || atlas.spaceFlight) {
      c.getContext('2d')?.clearRect(0, 0, c.width, c.height);
      return;
    }
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = window.innerWidth, H = window.innerHeight;
    if (c.width !== Math.floor(W * dpr) || c.height !== Math.floor(H * dpr)) {
      c.width = Math.floor(W * dpr); c.height = Math.floor(H * dpr);
    }
    const g = c.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, W, H);

    const px = atlas.localX, pz = atlas.localZ;
    const yaw = atlas.localYaw ?? 0;
    const scale = 2.4; // px per meter — a ~200 m read on a 1080p screen
    const R = Math.hypot(W, H) / (2 * scale) + 30;
    const cos = Math.cos(yaw), sin = Math.sin(yaw);
    // World → screen: translate to player, rotate so the camera heading points
    // UP (heading-up, the overlay's whole point), scale, center.
    const sx = (wx: number, wz: number): [number, number] => {
      const dx = wx - px, dz = wz - pz;
      const rx = dx * cos - dz * sin;
      const rz = dx * sin + dz * cos;
      // localYaw is the THREE convention (yaw 0 faces −Z ⇒ forward =
      // (−sin, −cos)) — a point ahead has rz = −d, so ahead-above-center is
      // y = center + rz·scale. (The first cut assumed atan2(dx,dz) heading
      // and flipped the axis — the world rendered behind-you-forward.)
      return [W / 2 + rx * scale, H / 2 + rz * scale];
    };

    const L = this.gather(R, px, pz);
    // Labels de-clutter: nearest-first, skip a label that would land on one
    // already drawn (the authentic screenshot is charmingly unreadable — we
    // keep the look without the spam).
    const drawn: Array<[number, number]> = [];
    this.overlayLabels = 0;
    const label = (x: number, y: number, text: string): void => {
      for (const [ox, oy] of drawn) { if (Math.abs(ox - x) < 90 && Math.abs(oy - y) < 13) return; }
      drawn.push([x, y]);
      this.overlayLabels++;
      g.font = '10px system-ui';
      g.fillStyle = 'rgba(5,10,16,.75)';
      const wpx = g.measureText(text).width;
      g.fillRect(x - wpx / 2 - 3, y - 9, wpx + 6, 12);
      g.fillStyle = '#e8f2fc';
      g.textAlign = 'center';
      g.fillText(text, x, y);
      g.textAlign = 'left';
    };

    // Building footprints — translucent green rotated squares (SWG green).
    g.save();
    for (const b of [...L.buildings].sort((a, bb) => (Math.hypot(a.x - px, a.z - pz) - Math.hypot(bb.x - px, bb.z - pz)))) {
      const [x, y] = sx(b.x, b.z);
      if (x < -60 || y < -60 || x > W + 60 || y > H + 60) continue;
      const s = 26; // footprint quad (uniform — the read matters, not the exact plot)
      g.save();
      g.translate(x, y);
      g.rotate(yaw);
      g.fillStyle = 'rgba(52,180,72,.34)';
      g.strokeStyle = 'rgba(80,220,100,.55)';
      g.fillRect(-s / 2, -s / 2, s, s);
      g.strokeRect(-s / 2, -s / 2, s, s);
      g.restore();
      label(x, y - 18, prettyKey(b.key));
    }
    g.restore();

    // Named venues (moved OFF the world 2026-08-13 — floating name bubbles
    // now live ONLY here): the nearest settlement's functional slots as gold
    // diamonds, civ-voiced names through the same de-clutter labeler.
    {
      const seed = this.seed;
      if (seed !== '') {
        let vsite: SettlementSite | null = null;
        let vbd = Infinity;
        for (const st of this.sites) {
          const d = (st.centerX - px) ** 2 + (st.centerZ - pz) ** 2;
          if (d < vbd) { vbd = d; vsite = st; }
        }
        if (vsite && vbd < 2200 * 2200) {
          if (this.venueSiteId !== vsite.id) {
            this.venueSiteId = vsite.id;
            this.venueSlots = townSlots(seed, vsite);
            this.venueCiv = civilizationOf(vsite.siteSeed);
          }
          for (const sl of this.venueSlots ?? []) {
            const [x, y] = sx(sl.x, sl.z);
            if (x < -60 || y < -60 || x > W + 60 || y > H + 60) continue;
            g.save();
            g.translate(x, y);
            g.rotate(Math.PI / 4);
            g.fillStyle = 'rgba(232,194,104,.5)';
            g.strokeStyle = 'rgba(255,220,140,.8)';
            g.fillRect(-5, -5, 10, 10);
            g.strokeRect(-5, -5, 10, 10);
            g.restore();
            label(x, y - 14, this.venueCiv === 'b' ? sl.nameB : sl.nameA);
          }
        }
      }
    }

    // Deed structures — violet footprints, labeled by type.
    for (const d of L.deeds) {
      const [x, y] = sx(d.x, d.z);
      if (x < -60 || y < -60 || x > W + 60 || y > H + 60) continue;
      g.save();
      g.translate(x, y);
      g.rotate(yaw);
      g.fillStyle = 'rgba(150,110,220,.32)';
      g.strokeStyle = 'rgba(180,138,232,.6)';
      g.fillRect(-11, -11, 22, 22);
      g.strokeRect(-11, -11, 22, 22);
      g.restore();
      label(x, y - 16, prettyKey(d.type));
    }

    // Ports — glyph + "Kind - Settlement" label (the SWG label format).
    for (const p of L.ports) {
      const [x, y] = sx(p.x, p.z);
      if (x < -80 || y < -80 || x > W + 80 || y > H + 80) continue;
      if (p.kind === 'shuttle') {
        g.fillStyle = 'rgba(232,169,78,.9)';
        g.beginPath(); g.moveTo(x, y - 8); g.lineTo(x + 7, y + 6); g.lineTo(x - 7, y + 6); g.closePath(); g.fill();
        label(x, y - 14, `Shuttleport - ${p.name}`);
      } else if (p.kind === 'spaceport') {
        g.fillStyle = 'rgba(127,196,232,.9)';
        g.beginPath(); g.moveTo(x, y - 9); g.lineTo(x + 9, y); g.lineTo(x, y + 9); g.lineTo(x - 9, y); g.closePath(); g.fill();
        label(x, y - 15, `Starport - ${p.name}`);
      } else {
        g.fillStyle = 'rgba(143,224,160,.9)';
        g.beginPath(); g.arc(x, y, 4, 0, Math.PI * 2); g.fill();
        label(x, y - 11, `Terminal - ${p.name}`);
      }
    }

    // Entities — SWG radar dots (yellow NPC, red aggro, blue players).
    g.fillStyle = '#e8d54a';
    for (const v of L.versonas) { const [x, y] = sx(v.x, v.z); g.beginPath(); g.arc(x, y, 3, 0, Math.PI * 2); g.fill(); }
    g.fillStyle = '#e5534b';
    for (const cr of L.creatures) { const [x, y] = sx(cr.x, cr.z); g.beginPath(); g.arc(x, y, 3.4, 0, Math.PI * 2); g.fill(); }
    g.fillStyle = '#6fe0ff';
    for (const p of L.players) { const [x, y] = sx(p.x, p.z); g.beginPath(); g.arc(x, y, 3.6, 0, Math.PI * 2); g.fill(); }

    // The player — fixed arrow pointing UP at screen center (heading-up view).
    g.save();
    g.translate(W / 2, H / 2);
    g.fillStyle = '#f4f8ff';
    g.strokeStyle = '#0a0f18';
    g.lineWidth = 1.2;
    g.beginPath(); g.moveTo(0, -9); g.lineTo(6.5, 8); g.lineTo(0, 4); g.lineTo(-6.5, 8); g.closePath();
    g.fill(); g.stroke();
    g.restore();
  }

  // ── Drawing ────────────────────────────────────────────────────────────────
  private draw(): void {
    const c = this.canvas;
    if (!c || !this.win?.isOpen()) return;
    const g = c.getContext('2d');
    if (!g) return;
    if (this.follow) { this.cx = atlas.localX; this.cz = atlas.localZ; }
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const W = this.cw(), H = this.ch();
    g.clearRect(0, 0, W, H);
    g.fillStyle = '#060a12';
    g.fillRect(0, 0, W, H);

    this.kickUnderlay();
    if (this.under) {
      const u = this.under;
      g.imageSmoothingEnabled = true;
      g.globalAlpha = 0.9;
      g.drawImage(u.img, this.sx(u.x0), this.sz(u.z0), (u.x1 - u.x0) * this.scale, (u.z1 - u.z0) * this.scale);
      g.globalAlpha = 1;
    }

    const L = this.gather();
    // Settlement buildings — small slate squares (the SWG overlay-map read).
    g.fillStyle = '#9fb2c8';
    for (const b of L.buildings) {
      const s = Math.max(2.5, Math.min(7, this.scale * 5));
      g.fillRect(this.sx(b.x) - s / 2, this.sz(b.z) - s / 2, s, s);
    }
    // Deed structures — violet squares with an outline.
    for (const d of L.deeds) {
      const s = Math.max(3.5, Math.min(9, this.scale * 7));
      g.fillStyle = '#b48ae8';
      g.strokeStyle = '#0a0f18';
      g.fillRect(this.sx(d.x) - s / 2, this.sz(d.z) - s / 2, s, s);
      g.strokeRect(this.sx(d.x) - s / 2, this.sz(d.z) - s / 2, s, s);
    }
    // Ports.
    for (const p of L.ports) {
      const x = this.sx(p.x), y = this.sz(p.z);
      if (p.kind === 'shuttle') {
        g.fillStyle = '#e8a94e';
        g.beginPath(); g.moveTo(x, y - 6); g.lineTo(x + 5.5, y + 4.5); g.lineTo(x - 5.5, y + 4.5); g.closePath(); g.fill();
      } else if (p.kind === 'spaceport') {
        g.fillStyle = '#7fc4e8';
        g.beginPath(); g.moveTo(x, y - 6.5); g.lineTo(x + 6.5, y); g.lineTo(x, y + 6.5); g.lineTo(x - 6.5, y); g.closePath(); g.fill();
      } else {
        g.fillStyle = '#8fe0a0';
        g.beginPath(); g.arc(x, y, 3.2, 0, Math.PI * 2); g.fill();
      }
    }
    // Entities — SWG radar dots.
    g.fillStyle = '#e8d54a';
    for (const v of L.versonas) { g.beginPath(); g.arc(this.sx(v.x), this.sz(v.z), 2.4, 0, Math.PI * 2); g.fill(); }
    g.fillStyle = '#e5534b';
    for (const cr of L.creatures) { g.beginPath(); g.arc(this.sx(cr.x), this.sz(cr.z), 2.8, 0, Math.PI * 2); g.fill(); }
    g.fillStyle = '#6fe0ff';
    for (const p of L.players) { g.beginPath(); g.arc(this.sx(p.x), this.sz(p.z), 3, 0, Math.PI * 2); g.fill(); }

    // Personal waypoints (own + THIS world only — the planet-aware filter):
    // in view = gold diamond + name; out of view = edge-clamped hint diamond.
    for (const w of myWaypoints(atlas.conn)) {
      let x = this.sx(w.posX), y = this.sz(w.posZ);
      const inView = x >= 0 && y >= 0 && x <= W && y <= H;
      if (!inView) {
        x = Math.max(10, Math.min(W - 10, x));
        y = Math.max(10, Math.min(H - 10, y));
      }
      drawWaypointMarker(g, x, y, inView ? w.name : '', !inView);
    }

    // The player: white arrow at the live position, rotated by heading
    // (north-up map, rotating arrow — the SWG planetary-map convention).
    const pxs = this.sx(atlas.localX), pzs = this.sz(atlas.localZ);
    g.save();
    g.translate(pxs, pzs);
    g.rotate(-(atlas.localYaw ?? 0));
    g.fillStyle = '#f4f8ff';
    g.strokeStyle = '#0a0f18';
    g.lineWidth = 1;
    g.beginPath(); g.moveTo(0, -7); g.lineTo(5, 6); g.lineTo(0, 3); g.lineTo(-5, 6); g.closePath();
    g.fill(); g.stroke();
    g.restore();

    // Scale bar.
    const meters = 100 / this.scale;
    const nice = Math.pow(10, Math.floor(Math.log10(meters)));
    const barM = meters / nice >= 5 ? nice * 5 : meters / nice >= 2 ? nice * 2 : nice;
    const barPx = barM * this.scale;
    g.fillStyle = 'rgba(230,240,255,.75)';
    g.fillRect(12, H - 16, barPx, 2);
    g.font = '10px system-ui';
    g.fillText(barM >= 1000 ? `${barM / 1000} km` : `${barM} m`, 12, H - 22);
  }

  dispose(): void {
    this.stopLoops();
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.overlayRaf) cancelAnimationFrame(this.overlayRaf);
    this.overlayCanvas?.remove();
    this.win?.root.remove();
  }
}
